import React, { useState } from 'react';
import { ShieldAlert, Lock, UserCircle, Fingerprint, QrCode } from 'lucide-react';

interface LoginProps {
  onLogin: (credentials: { email: string; password: string }) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showMFA, setShowMFA] = useState(false);
  const [mfaType, setMfaType] = useState<'qr' | 'biometric' | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulamos la primera fase de autenticación
    if (email && password) {
      setShowMFA(true);
    }
  };

  const handleMFASelection = (type: 'qr' | 'biometric') => {
    setMfaType(type);
    // En un escenario real, aquí se generaría el código QR o se iniciaría la autenticación biométrica
    setTimeout(() => {
      onLogin({ email, password });
    }, 2000);
  };

  return (
    <div className="w-full max-w-md mx-auto p-6 bg-cyber-dark-blue bg-opacity-80 backdrop-blur-md rounded-lg shadow-xl border border-cyber-blue/30">
      <div className="flex justify-center mb-6">
        <ShieldAlert className="text-cyber-blue h-16 w-16" />
      </div>
      <h2 className="text-2xl font-bold text-white text-center mb-6">Sistema de Autenticación Segura</h2>
      
      {!showMFA ? (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-cyber-light mb-1">Correo Electrónico</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <UserCircle className="h-5 w-5 text-cyber-blue" />
              </div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-cyber-gray/30 text-white placeholder-gray-400 block w-full pl-10 pr-3 py-2 border border-cyber-blue/30 rounded-md focus:outline-none focus:ring-2 focus:ring-cyber-blue"
                placeholder="usuario@empresa.com"
                required
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-cyber-light mb-1">Contraseña</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-cyber-blue" />
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-cyber-gray/30 text-white placeholder-gray-400 block w-full pl-10 pr-3 py-2 border border-cyber-blue/30 rounded-md focus:outline-none focus:ring-2 focus:ring-cyber-blue"
                placeholder="••••••••"
                required
              />
            </div>
          </div>
          
          <div>
            <button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-cyber-blue hover:bg-cyber-blue/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyber-blue transition-colors duration-200"
            >
              Iniciar Sesión
            </button>
          </div>
          
          <div className="text-sm text-center">
            <p className="text-cyber-light">
              Protegido con encriptación AES-256
            </p>
          </div>
        </form>
      ) : (
        <div className="space-y-6">
          <div className="text-center text-cyber-light">
            <p className="mb-4">Verificación en dos pasos requerida</p>
            <p className="text-sm mb-4">Seleccione su método de autenticación preferido:</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => handleMFASelection('qr')}
              className={`p-4 border ${mfaType === 'qr' ? 'border-cyber-blue bg-cyber-blue/20' : 'border-cyber-gray/50 bg-cyber-gray/20'} rounded-lg flex flex-col items-center justify-center hover:bg-cyber-gray/30 transition-colors duration-200`}
            >
              <QrCode className="h-8 w-8 text-cyber-blue mb-2" />
              <span className="text-sm text-white">Código QR</span>
              {mfaType === 'qr' && (
                <div className="mt-3 animate-pulse">
                  <div className="w-24 h-24 bg-white p-1 rounded-md mx-auto">
                    <div className="w-full h-full bg-black grid grid-cols-4 grid-rows-4 gap-1">
                      {/* Simulación simple de un QR */}
                      {Array(16).fill(0).map((_, i) => (
                        <div key={i} className={`${Math.random() > 0.5 ? 'bg-white' : 'bg-black'}`}></div>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-center mt-2 text-cyber-light">Escaneando...</p>
                </div>
              )}
            </button>
            
            <button
              onClick={() => handleMFASelection('biometric')}
              className={`p-4 border ${mfaType === 'biometric' ? 'border-cyber-blue bg-cyber-blue/20' : 'border-cyber-gray/50 bg-cyber-gray/20'} rounded-lg flex flex-col items-center justify-center hover:bg-cyber-gray/30 transition-colors duration-200`}
            >
              <Fingerprint className="h-8 w-8 text-cyber-blue mb-2" />
              <span className="text-sm text-white">Biométrico</span>
              {mfaType === 'biometric' && (
                <div className="mt-3 animate-pulse">
                  <div className="w-16 h-16 rounded-full border-2 border-cyber-blue flex items-center justify-center mx-auto">
                    <Fingerprint className="h-10 w-10 text-cyber-blue animate-pulse" />
                  </div>
                  <p className="text-xs text-center mt-2 text-cyber-light">Verificando...</p>
                </div>
              )}
            </button>
          </div>
          
          <div className="text-xs text-center text-cyber-light mt-4">
            <p>Sistema protegido con autenticación multi-factor</p>
            <p>Conforme con GDPR y normativas de protección de datos</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;